import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowDownLeft,
  ArrowUpRight,
  Check,
  Clock,
  Phone,
  MessageCircle,
  type LucideIcon,
} from "lucide-react";

import { Card, CardHeader, Row } from "@/components/Card";
import Button from "@/components/Button";
import { useApp } from "@/App";
import {
  payments,
  feeBreakdown,
  type Payment,
  type PaymentStatus,
} from "@/data/mock";
import { school } from "@/theme/school";

// ─────────────────────────────────────────────────────────────────
// Iratation High School OS — finance.
//
// Two tabs, both on the same page:
//
//   Payments  — the ledger of every fee transaction this term.
//   Structure — the term's fee breakdown by class.
//
// The tab strip uses the same `layoutId` technique as the tab
// bar, but at a smaller scale — a 2px underline instead of a
// pill. This is the iOS segmented-control feel adapted for a
// full-width content page.
//
// The page is deliberately the densest screen in the demo. A
// bursar's day is spent reading this table, so it earns more
// visual weight than the student list or the dashboard.
// ─────────────────────────────────────────────────────────────────

type Tab = "payments" | "structure";

const STATUS: Record<
  PaymentStatus,
  { label: string; fg: string; bg: string; Icon: LucideIcon }
> = {
  paid:    { label: "Paid",    fg: "#248A3D", bg: "rgba(52, 199, 89, 0.14)",  Icon: Check },
  pending: { label: "Pending", fg: "#C93400", bg: "rgba(255, 149, 0, 0.14)", Icon: Clock },
  failed:  { label: "Failed",  fg: "#C93400", bg: "rgba(255, 59, 48, 0.14)", Icon: Clock },
};

export default function Finance() {
  const role = useApp((s) => s.role);
  const [tab, setTab] = useState<Tab>("payments");

  const total = useMemo(
    () =>
      payments
        .filter((p) => p.status === "paid")
        .reduce((sum, p) => sum + p.raw, 0),
    []
  );

  const isBursarOrDirector = role === "bursar" || role === "director";

  return (
    <div className="px-5 pb-8 pt-5">
      {/* ─── Heading ───────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
      >
        <p className="text-ios-footnote text-ios-secondary">
          Term 2, 2026 · {payments.length} transactions
        </p>
        <h1 className="mt-0.5 font-tabular text-ios-title-1 font-bold tracking-ios text-ios-label">
          {formatUGX(total)}
        </h1>
        <p className="mt-0.5 text-ios-caption-1 text-ios-secondary">
          Collected this term
        </p>
      </motion.div>

      {/* ─── Segmented tabs ──────────────────────────────────
          Two tabs, an underline that slides. Same layoutId
          pattern as the bottom tab bar, so the motion
          vocabulary is consistent across the app — a user
          who learns the tab bar animation recognises this
          one instantly. */}
      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.05, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="mt-4 flex gap-1 rounded-ios-md bg-ios-fill p-1"
      >
        {(["payments", "structure"] as const).map((t) => {
          const active = tab === t;
          return (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className="relative flex-1 rounded-ios py-1.5 text-ios-footnote font-semibold capitalize transition-colors"
              style={{ color: active ? "#1C1C1E" : "#8E8E93" }}
            >
              {active && (
                <motion.span
                  layoutId="finance-tab-pill"
                  className="absolute inset-0 rounded-ios bg-ios-card shadow-ios-sm"
                  transition={{ type: "spring", stiffness: 480, damping: 34, mass: 0.7 }}
                />
              )}
              <span className="relative z-10">{t}</span>
            </button>
          );
        })}
      </motion.div>

      {/* ─── Tab content ───────────────────────────────────── */}
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.22, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          {tab === "payments" ? (
            <PaymentsTab canRecord={isBursarOrDirector} />
          ) : (
            <StructureTab />
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

// ─── Payments tab ───────────────────────────────────────────────

function PaymentsTab({ canRecord }: { canRecord: boolean }) {
  return (
    <>
      {canRecord && (
        <div className="mt-4 flex gap-2">
          <Button
            variant="primary"
            size="md"
            fullWidth
            leadingIcon={ArrowDownLeft}
          >
            Record payment
          </Button>
          <Button
            variant="secondary"
            size="md"
            fullWidth
            leadingIcon={ArrowUpRight}
          >
            Send reminder
          </Button>
        </div>
      )}

      <div className="mt-4 overflow-hidden rounded-ios-lg bg-ios-card shadow-ios">
        {payments.map((p, i) => (
          <PaymentRow
            key={p.id}
            payment={p}
            separator={i < payments.length - 1}
            index={Math.min(i, 8)}
          />
        ))}
      </div>

      <p className="mt-4 text-center text-ios-caption-2 text-ios-secondary">
        Showing {payments.length} of {payments.length} transactions
      </p>
    </>
  );
}

// ─── Payment row ────────────────────────────────────────────────

function PaymentRow({
  payment,
  separator,
  index,
}: {
  payment: Payment;
  separator: boolean;
  index: number;
}) {
  const status = STATUS[payment.status];
  const { Icon } = status;

  const initials = payment.student
    .split(" ")
    .map((x) => x[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{
        opacity: 1,
        y: 0,
        transition: {
          duration: 0.22,
          ease: [0.25, 0.46, 0.45, 0.94],
          delay: index * 0.02,
        },
      }}
      className={[
        "flex items-center gap-3 px-4 py-3",
        separator ? "hairline-b" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <span
        className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-[12px] font-semibold"
        style={{ backgroundColor: status.bg, color: status.fg }}
      >
        {initials}
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-ios-body font-medium text-ios-label">
            {payment.student}
          </span>
          <span
            className="inline-flex shrink-0 items-center gap-0.5 rounded-ios-pill px-1.5 py-0.5 text-ios-caption-2 font-semibold"
            style={{ backgroundColor: status.bg, color: status.fg }}
          >
            <Icon size={9} strokeWidth={3} absoluteStrokeWidth />
            {status.label}
          </span>
        </div>
        <p className="mt-0.5 truncate text-ios-caption-1 text-ios-secondary">
          {payment.guardian} · {payment.method}
        </p>
      </div>

      <div className="shrink-0 text-right">
        <p className="font-tabular text-ios-subhead font-semibold text-ios-label">
          {payment.amount}
        </p>
        <p className="mt-0.5 text-ios-caption-2 text-ios-tertiary">
          {payment.when}
        </p>
      </div>
    </motion.div>
  );
}

// ─── Structure tab ──────────────────────────────────────────────

function StructureTab() {
  return (
    <>
      <Card className="mt-4" index={0}>
        <CardHeader
          title="Term 2 fees"
          subtitle="Per student, all classes"
        />
        <div className="px-4 pb-4">
          {feeBreakdown.map((f, i) => {
            const totalFees = feeBreakdown.reduce((sum, fee) => sum + fee.amount, 0);
            const pct = (f.amount / totalFees) * 100;
            return (
              <div
                key={f.label}
                className={[
                  "py-3",
                  i < feeBreakdown.length - 1 ? "hairline-b" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <div className="flex items-baseline justify-between">
                  <span className="text-ios-subhead text-ios-label">
                    {f.label}
                  </span>
                  <span className="font-tabular text-ios-subhead font-semibold text-ios-label">
                    {formatUGX(f.amount)}
                  </span>
                </div>
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-ios-fill">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${pct}%` }}
                    transition={{
                      duration: 0.6,
                      delay: 0.1 + i * 0.06,
                      ease: [0.25, 0.46, 0.45, 0.94],
                    }}
                    className="h-full rounded-full"
                    style={{ backgroundColor: "var(--brand-navy)" }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <Card className="mt-5" index={1}>
        <CardHeader title="Payment channels" subtitle="How guardians pay" />
        <div>
          <Row
            title="MTN Mobile Money"
            subtitle="62% of transactions"
            leading={Phone}
            leadingTone="orange"
            trailing="UGX 48.2M"
            separator
          />
          <Row
            title="Airtel Money"
            subtitle="31% of transactions"
            leading={Phone}
            leadingTone="red"
            trailing="UGX 24.1M"
            separator
          />
          <Row
            title="Bank transfer"
            subtitle="6% of transactions"
            leading={MessageCircle}
            leadingTone="blue"
            trailing="UGX 4.7M"
            separator={false}
          />
        </div>
      </Card>

      <p className="mt-4 text-center text-ios-caption-2 text-ios-secondary">
        {school.name} · Fees desk
      </p>
    </>
  );
}

// ─── Formatting ─────────────────────────────────────────────────

function formatUGX(amount: number): string {
  return `UGX ${amount.toLocaleString("en-UG")}`;
}